import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, PenLine, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { api, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";
import { useT } from "../lib/i18n";
import { CONSENT_LANGS } from "../lib/helpers";
import { Field, NativeSelect } from "./Common";

export default function ConsentDialog({ open, onOpenChange, client, appointmentId, templateId, onDone }) {
  const { templates } = useApp();
  const { t } = useT();
  const [tid, setTid] = useState("");
  const [language, setLanguage] = useState("en");
  const [link, setLink] = useState("");

  useEffect(() => {
    if (open) {
      setTid(templateId || templates[0]?.id || "");
      setLanguage(client?.preferred_language || "en");
      setLink("");
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const tpl = templates.find((x) => x.id === tid);
  const langs = Object.keys(tpl?.versions || {});

  const go = async (mode) => {
    const win = mode === "sign" ? window.open("", "_blank") : null;
    try {
      const { data } = await api.post("/consents/requests", { client_id: client.id, template_id: tid, language, appointment_id: appointmentId, send_email: mode === "email" });
      setLink(data.url);
      if (mode === "sign") {
        if (win) win.location.href = data.url;
        else window.location.assign(data.url);
      }
      if (mode === "email") data.emailed ? toast.success("Consent request emailed") : toast.warning("Email could not be sent; copy the link instead");
      onDone?.();
    } catch (e) { win?.close(); toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="consent-dialog">
        <DialogHeader><DialogTitle>{t("request_consent")}</DialogTitle></DialogHeader>
        <div className="grid gap-4">
          <Field label={t("consent_form")}>
            <NativeSelect testId="consent-template-select" value={tid} onChange={setTid} options={templates.filter((x) => x.active).map((x) => ({ value: x.id, label: x.name }))} />
          </Field>
          <Field label={t("language")}>
            <NativeSelect testId="consent-language-select" value={langs.includes(language) ? language : langs[0] || ""} onChange={setLanguage}
              options={langs.map((l) => ({ value: l, label: CONSENT_LANGS[l] || l }))} />
          </Field>
          <div className="grid gap-2">
            <Button data-testid="consent-sign-now-btn" className="rounded-full" disabled={!tid} onClick={() => go("sign")}><PenLine className="h-4 w-4 mr-2" />{t("sign_now")}</Button>
            <Button data-testid="consent-email-btn" variant="outline" className="rounded-full" disabled={!tid || !client?.email} onClick={() => go("email")}><Mail className="h-4 w-4 mr-2" />{t("send_link")}</Button>
          </div>
          {link && (
            <button data-testid="consent-copy-link-btn" onClick={() => { navigator.clipboard?.writeText(link); toast.success("Link copied"); }}
              className="flex items-center gap-2 text-xs text-primary break-all text-left"><Copy className="h-3.5 w-3.5 shrink-0" />{link}</button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
